<template lang="pug">
  .templates
      .template-type()
        label {{ $t('settings_tab.admin_tab.message_template.type') }}
        aiq-select(placeholder="Type"
                  size="small"
                  :disabled="messageTemplateMode === 'edit' || isViewMode"
                  @change="changeTemplateType"
                  v-model="templateType")
          aiq-option(v-for="type in templateOptions", :key="type.value", :label="type.label", :value="type.value")
      .selection(v-if="templateType !== ''")
        aiq-row(v-if="!inModal")
          label {{ $t('settings_tab.admin_tab.message_template.select_existing') }}
        aiq-row(v-if="!inModal")
          aiq-col(:span="21")
            aiq-select(placeholder="Template"
                      size="small"
                      :disabled="isViewMode"
                      @change="selectedExistingTemplate"
                      v-model="selectedTemplateTitle")
              aiq-option(v-for="name in templateNames", :key="name", :label="name", :value="name")
          aiq-col(:span="1")
          aiq-col(:span="2")
            aiq-button(type="success" size="small" @click="onNewTemplate") {{ $t('settings_tab.admin_tab.email_tab.email_template.add') }}
        .template(v-if="templateType !== ''")
            .template-details(v-if="(mode === 'add' || (mode === 'edit' && !!selectedTemplateTitle))")
                .content
                  aiq-row
                      .is-required
                      label {{ $t('settings_tab.admin_tab.message_template.content_title') }}
                      aiq-input(v-model="template.title" @change="fieldChanged" :disabled="mode === 'edit' || isViewMode || messageTemplateMode === 'edit'")
                .content(v-if="getCurrentTemplate() === 'notification'")
                  aiq-row
                    label {{ $t('settings_tab.admin_tab.message_template.notification_type') }}
                    aiq-select(
                      v-model="template.notification_type"
                      :disabled="mode === 'edit' || isViewMode || messageTemplateMode === 'edit'"
                      size="small"
                      clearable)
                      aiq-option(v-for="item in notificationTypes"
                              :key="item"
                              :label="item.label"
                              :value="item.value")
                .content(v-if="getCurrentTemplate() === 'sms'")
                  aiq-row
                    label {{ $t('settings_tab.admin_tab.message_template.sms') }}
                    aiq-select(
                      v-model="template.phone"
                      :disabled="isViewMode"
                      @change="fieldChanged"
                      size="small"
                      clearable)
                      aiq-option(v-for="item in smsNumbers"
                              :key="item.id"
                              :label="item.label"
                              :value="item.value")
                .content
                  aiq-row
                      .is-required
                      label {{ $t('settings_tab.admin_tab.message_template.content') }}
                      aiq-input(v-if="showRegularTextArea"
                        v-model="template.content"
                        :disabled="isViewMode"
                        @change="fieldChanged"
                        class="textarea"
                        type="textarea")
                      .el-input(v-else)
                        .custom-rich-text-container
                          rich-text(@update="onUpdateRichTextEditor"
                                  :showImageUpload="true"
                                  :readOnlyMode="isViewMode"
                                  :inChat="false")
                .content(v-if="getCurrentTemplate() !== 'notification'")
                  aiq-row
                      label {{ $t('settings_tab.admin_tab.message_template.tags') }}
                        aiq-tooltip(effect="dark"
                          :show-after="1000"
                          :content="$t('settings_tab.admin_tab.message_template.tags_tooltip')")
                          fa-icon.question-mark(:icon="['fas', 'circle-question']")
                  aiq-row
                      aiq-col(:span="24")
                        aiq-select(v-model="template.tags"
                          size="small"
                          multiple
                          value-key="id"
                          :disabled="isViewMode"
                          @change="fieldChanged"
                          name="tags"
                          class="tag-selection"
                          :reserve-keyword="false")
                          aiq-option(v-for="item in tagList"
                                  :key="item"
                                  :label="item"
                                  :value="item"
                                  filterable
                                  default-first-option)
                  .required-footer(v-if="!inModal || messageTemplateMode !== 'view'")
                    .is-required-btn {{ $t('settings_tab.admin_tab.message_template.required') }}
                aiq-row
                  .content-footer
                    aiq-button(@click="onCancel") {{isViewMode ? $t('global.close') : $t('global.cancel') }}
                    aiq-button(v-if="this.selectedTemplateTitle && !inModal" type="danger" @click="onDelete") {{ $t('global.delete') }}
                    aiq-button(v-if="!inModal || messageTemplateMode !== 'view'" type="primary" class="save-btn" @click="Save" :disabled="!shouldSaveButtonEnabled") {{ $t('global.save') }}
  </template>
<script>
import get from 'lodash/get';
import cloneDeep from 'lodash/cloneDeep';
import isEqual from 'lodash/isEqual';
import isEmpty from 'lodash/isEmpty';
import { TAG_LIMIT } from '@/constants/pagination';
import { mapState } from 'vuex';
import mitt from 'mitt';
import RichText from '@/components/RichText/RichText.vue';

const NotificationTypes = {
  EMAIL: 'email',
  DASHBOARD: 'dashboard',
  EMAIL_AND_DASHBOARD: 'email_and_dashboard',
};
const TemplateTypes = {
  LYNQ_UP_MESSAGE: 'Lynq Up Message',
  SMS: 'SMS',
  NOTIFICATIONS: 'Notifications',
};

const DURATION = 1500;
const emptyTemplate = {
  title: '',
  content: '',
  phone: '',
  tags: [],
};
export default {
  name: 'message-template',
  components: {
    RichText,
  },
  provide() {
    return {
      richTextEventEmitter: this.richTextEventEmitter,
    };
  },
  props: {
    templates: {
      type: Object,
      default: () => ({}),
    },
    selectedTemplateName: {
      type: String,
      default: '',
    },
    inModal: {
      type: Boolean,
      default: false,
    },
    messageTemplateMode: {
      type: String,
      default: '',
    },
    channel: {
      type: String,
      default: TemplateTypes.LYNQ_UP_MESSAGE,
    },
  },

  async mounted() {
    try {
      const data = await this.$store.dispatch('tags/getTagsList', [{ limit: TAG_LIMIT }]);
      this.tagList = get(data, 'data.models', []).map(e => e.name);

      //get config
      const config = await this.$store.dispatch('configs/getConfigurations', {
        config: 'outboundSms',
      });
      this.outBoundConfig = config.data;
      this.getSmsNumbers();
      if (this.inModal) {
        if (this.messageTemplateMode === 'edit') {
          this.mode = 'edit';
          this.selectedTemplateTitle = this.selectedTemplateName;
          this.setTemplateType();
          this.selectedExistingTemplate();
        } else if (this.messageTemplateMode === 'add') {
          this.onNewTemplate();
        } else {
          this.selectedTemplateTitle = this.selectedTemplateName;
          this.setTemplateType();
          this.selectedExistingTemplate();
        }
      }
    } catch (err) {
      this.$aiq.notify.error('Error fetching tags and Configurations');
    }

  },
  data() {
    return {
      mode: '',
      selectedTemplateTitle: null,
      template: cloneDeep(emptyTemplate),
      templateType: TemplateTypes.LYNQ_UP_MESSAGE,
      notificationTypes: [{
        value: NotificationTypes.EMAIL,
        label: 'Email',
      }, {
        value: NotificationTypes.DASHBOARD,
        label: 'Dashboard',
      },
      {
        value: NotificationTypes.EMAIL_AND_DASHBOARD,
        label: 'Email and Dashboard',
      },
      ],
      tagList: [],
      outBoundConfig: null,
      smsNumbers: [],
      defaultSmsNumber: '',
      richTextEventEmitter: mitt(),
    };
  },
  methods: {
    onNewTemplate() {
      this.mode = 'add';

      if (this.inModal) {
        switch (this.channel) {
          case 'sms':
            this.templateType = TemplateTypes.SMS;
            break;
          case 'notification':
            this.templateType = TemplateTypes.NOTIFICATIONS;
            break;
          default:
            this.templateType = TemplateTypes.LYNQ_UP_MESSAGE;
        }
      }

      this.template = cloneDeep({ ...emptyTemplate, template_type: this.getCurrentTemplate()});
      if (this.getCurrentTemplate() === 'notification') {
        this.template.notification_type = NotificationTypes.EMAIL;
      }
      if (this.getCurrentTemplate() === 'sms') {
        this.template.phone = this.defaultSmsNumber;
      }
      else {
        this.$nextTick(() => this.richTextEventEmitter.emit('setText', this.template.content));
      }
      this.selectedTemplateTitle = null;
    },
    setTemplateType() {
      const template = this.templates.find(template => template.title === this.selectedTemplateName);
      if (template.template_type === 'sms') {
        this.templateType = TemplateTypes.SMS;
      } else if (template.template_type === 'notification') {
        this.templateType = TemplateTypes.NOTIFICATIONS;
      } else {
        this.templateType = TemplateTypes.LYNQ_UP_MESSAGE;
      }
    },
    fieldChanged() {
      this.$emit('fieldChanged');
    },

    changeTemplateType() {
      if (this.inModal) {
        this.onNewTemplate();
      } else {
        this.mode = '';
        this.selectedTemplateTitle = null;
      }
    },
    getCurrentTemplate() {
      switch (this.templateType) {
        case TemplateTypes.SMS:
          return 'sms';
        case TemplateTypes.NOTIFICATIONS:
          return 'notification';
        default:
          return 'webchat';
      }
    },

    selectedExistingTemplate() {
      this.selectedTemplate(this.selectedTemplateTitle);
    },
    onCancel() {
      if (this.inModal) {
        this.$emit('close');
      } else {
        if (this.mode === 'edit') {
          this.selectedTemplateTitle = null;
        } else if (this.mode === 'add') {
          this.template = cloneDeep({ ...emptyTemplate, template_type: this.getCurrentTemplate() });
        }
      }
    },
    Update(templates, message) {
      if (this.inModal) {
        return this.$emit('update', templates, message, {mode: this.messageTemplateMode, template: this.template});
      } else {
        return this.$emit('update', templates, message);
      }
    },
    async Save() {
      const newTemplates = cloneDeep(this.templates);
      const message = this.mode === 'edit' ?
        this.$t('settings_tab.admin_tab.message_template.message_temp_updated') :
        this.$t('settings_tab.admin_tab.message_template.message_temp_created');

      if (this.mode === 'edit') {
        const index = this.templates.findIndex(t => (t.title === this.selectedTemplateTitle && t.template_type === this.getCurrentTemplate()));
        newTemplates[index] = this.template;

      } else if (this.mode === 'add') {
        newTemplates.push(this.template);
        setTimeout(() => {
          this.changeTemplateType();
        }, DURATION);
      }
      return this.Update(newTemplates, message);
    },

    async confirmDelete() {
      try {
        await this.$aiq.confirm(
          this.$t('settings_tab.admin_tab.email_tab.email_template.on_delete.title'),
          this.$t('settings_tab.admin_tab.email_tab.email_template.on_delete.body'),
          {
            confirmButtonText: this.$t('global.continue'),
            cancelButtonText: this.$t('global.cancel'),
          },
        );
        return true;
      } catch (err) {
        return false;
      }
    },

    async onDelete() {
      try {
        const confirm = await this.confirmDelete();
        if (!confirm) return;
        const newTemplates = cloneDeep(this.templates);
        const index = this.templates.findIndex(t => (t.title === this.selectedTemplateTitle && t.template_type === this.getCurrentTemplate()));

        let inUse = false;
        if (index !== -1) {

          //check if template is being used in campaign
          const response = await this.$store.dispatch('campaign/validateTemplate', newTemplates[index].title);
          inUse = get(response, 'data.template_in_use', false);

          //if it is being used show info and skip delete
          if (inUse) {
            this.$aiq.notify.info(this.$t('settings_tab.admin_tab.message_template.message_temp_in_use'));
            return;
          } else {
            newTemplates.splice(index, 1);
          }
        }
        this.selectedTemplateTitle = null;
        this.template = cloneDeep({ ...emptyTemplate, template_type: this.getCurrentTemplate() });
        return this.Update(newTemplates, this.$t('settings_tab.admin_tab.message_template.message_temp_deleted'));
      } catch (e) {
        this.$aiq.notify.error(this.$t('settings_tab.admin_tab.message_template.delete_error'));
        return null;
      }
    },
    selectedTemplate(newVal) {
      if (!isEmpty(newVal)) {
        this.template = cloneDeep(this.templates.filter(t => t.template_type === this.getCurrentTemplate()).find(t => t.title === newVal));
        this.mode = 'edit';
      } else {
        this.template = cloneDeep({ ...emptyTemplate, template_type: this.getCurrentTemplate() });
      }
      this.template.phone = this.template.phone || this.defaultSmsNumber;

      // Initialize rich text editor when template is initialized
      if (this.getCurrentTemplate() !== 'sms') {
        this.$nextTick(() => this.richTextEventEmitter.emit('setText', this.template.content));
      }
    },

    getSmsNumbers() {
      const smsNumbers = get(this.outBoundConfig, 'sms_phone_numbers', []);
      const result = smsNumbers.map((number, index) => ({
        label: `${number}`,
        value: number,
        id: index,
      }));
      if (result.length) {
        this.defaultSmsNumber = result[0].value;
      }

      const sendingNumber = get(this.outBoundConfig, 'sending_phone_number');
      if (sendingNumber) {
        result.push({
          label: `${sendingNumber} (Outbound Only)`,
          value: sendingNumber,
          id: smsNumbers.length,
        });
      }
      this.smsNumbers = result;
    },
    onUpdateRichTextEditor(msgData) {
      if (msgData.text !== '' && this.messageTemplateMode !== 'view') {
        this.fieldChanged();
      }
      this.template.content = msgData.text === '' ? '' : msgData.html;
    },
  },
  emits: ['update', 'close', 'fieldChanged'],
  computed: {
    ...mapState({
      featureFlags: state => state.featureFlags.FEATURE_FLAGS,
    }),
    templateOptions() {
      return [
        ...(this.featureFlags.ENABLE_AGENT_CAMPAIGN_NOTIFICATION ? [{
          value: TemplateTypes.NOTIFICATIONS,
          label: this.$t('settings_tab.admin_tab.message_template.agent_notification'),
        }] : []),
        {
          value: TemplateTypes.LYNQ_UP_MESSAGE,
          label: this.$t('settings_tab.admin_tab.message_template.lynq_up_message'),
        },
        {
          value: TemplateTypes.SMS,
          label: this.$t('settings_tab.admin_tab.message_template.sms_option'),
        },
      ];
    },
    templateNames() {
      return this.templates.filter(t => t.template_type === this.getCurrentTemplate()).map(t => t.title);
    },
    shouldSaveButtonEnabled() {
      return this.isFormatValid && (this.isTemplateChanged || this.canAddNewItem);
    },
    isFormatValid() {
      return !isEmpty(this.template.title)
        && !isEmpty(this.template.content);
    },

    showRegularTextArea() {
      if (this.getCurrentTemplate() === 'sms') {
        return true;
      }
      return false;
    },

    isViewMode() {
      return this.messageTemplateMode === 'view';
    },

    isTemplateChanged() {
      return this.mode === 'edit' && this.selectedTemplateTitle !== null
        && !isEqual(this.template, this.templates.filter(t => t.template_type === this.getCurrentTemplate()).find(t => this.selectedTemplateTitle === t.title));
    },
    canAddNewItem() {
      return this.selectedTemplateTitle === null && this.mode === 'add';
    },
  },
};
</script>
<style lang="scss" scoped>
@import "src/styles/aiq-mixins.scss";
:deep(.el-textarea__inner) {
  height: 250px;
}

:deep(.el-row) {
background-color: $aiq-bgc-list-item;
}

:deep(.el-select__wrapper) {
  height: 30px;
}

.question-mark {
  padding-left: 8px;
}

.content-footer {
  width: 100%;
  display: flex;
  justify-content: flex-end;
  padding-top: 20px;
}

.required-footer {
  padding: 10px;
  padding-right: 3px;
  width: 100%;
  text-align: right;
  margin-bottom: -10px;
  background-color: white;
}

.phone_numbe {
  margin-top: 15px;
}

.selection {
  margin-top: 15px;
}

.content {
  margin-top: 15px;
}

.is-required::after {
  content: "*";
  color: #FF0000;
}

.is-required-btn::before {
  color: #FF0000;
  margin-left: 5px;
  content: "*";
}

.is-required-btn {
  color: #FF0000;
}

.is-required+label {
  margin-left: 5px;
}

:deep(.el-input__inner) {
  height: 30px;
}
</style>